import React, { useState, useEffect } from 'react'
import { useParams, Link } from '@tanstack/react-router'
import { Card, Button, StatusBadge, PageHeader, CourierDetailContent } from '@/shared/ui'
import { useUserDetails, useUpdateUserStatus, useUpdateUserRole, useCourierTariffSummary, useModerationHistory } from '@/shared/hooks'
import { useAuth } from '@/shared/contexts'
import { USER_STATUS, USER_ROLE, type ApiError } from '@/shared/api'
import { ArrowLeftIcon } from '@heroicons/react/24/outline'

export const CourierDetailsPage: React.FC = () => {
  const { hasPermission, isSuperadmin } = useAuth()
  const { userId } = useParams({ from: '/couriers/$userId' })
  const userIdNumber = Number(userId)
  const { data: user, isLoading, error } = useUserDetails(userIdNumber, {
    enabled: !isNaN(userIdNumber) && userIdNumber > 0
  })
  const updateUserStatus = useUpdateUserStatus()
  const updateUserRole = useUpdateUserRole()
  const { data: tariffSummary } = useCourierTariffSummary(userIdNumber)
  const { data: history, isLoading: isHistoryLoading } = useModerationHistory(
    userIdNumber,
    !isNaN(userIdNumber) && userIdNumber > 0
  )
  const [isUpdating, setIsUpdating] = useState(false)
  const [selectedRole, setSelectedRole] = useState<number>(USER_ROLE.COURIER)

  useEffect(() => {
    if (user && (user.role === USER_ROLE.COURIER || user.role === USER_ROLE.EXTERNAL_COURIER)) {
      setSelectedRole(user.role)
    }
  }, [user])

  const handleStatusChange = async (newStatus: number, comment: string) => {
    try {
      setIsUpdating(true)
      await updateUserStatus.mutateAsync({
        userId: Number(userId),
        request: { status: newStatus, comment }
      })
    } finally {
      setIsUpdating(false)
    }
  }

  const handleRoleChange = async () => {
    try {
      await updateUserRole.mutateAsync({
        userId: Number(userId),
        request: { role: selectedRole }
      })
    } catch (error) {
      // Ошибка уже обработана в хуке (включая 400 — активные заказы)
    }
  }

  if (isLoading) {
    return (
      <>
        <PageHeader 
          title="Загрузка..." 
          description="Загрузка данных пользователя"
        >
          <Link to="/couriers">
            <Button variant="secondary" size="sm">
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Назад
            </Button>
          </Link>
        </PageHeader>
        <div className="flex-1 p-6">
          <Card>
            <div className="flex items-center justify-center py-12">
              <div className="flex items-center space-x-3">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-600"></div>
                <span className="text-gray-500">Загрузка данных пользователя...</span>
              </div>
            </div>
          </Card>
        </div>
      </>
    )
  }

  if (error || !user) {
    const apiError = error as unknown as ApiError
    return (
      <>
        <PageHeader 
          title="Ошибка" 
          description="Пользователь не найден"
        >
          <Link to="/couriers">
            <Button variant="secondary" size="sm">
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Назад
            </Button>
          </Link>
        </PageHeader>
        <div className="flex-1 p-6">
          <Card>
            <div className="text-center py-12">
              <h3 className="text-lg font-medium text-red-600 mb-2">
                Пользователь не найден
              </h3>
              <p className="text-gray-500 mb-4">
                {apiError?.detail || 'Не удалось загрузить информацию о пользователе'}
              </p>
              <Link to="/couriers">
                <Button variant="secondary">
                  <ArrowLeftIcon className="h-4 w-4 mr-2" />
                  Вернуться к списку
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </>
    )
  }

  // Статус администратора может менять только суперадмин: делегированная
  // роль с правом couriers.change_status не должна блокировать админов.
  const canChangeStatus = hasPermission('couriers', 'change_status')
    && (user.role !== USER_ROLE.ADMIN || isSuperadmin)

  return (
    <>
      <PageHeader 
        title={user.first_name && user.last_name 
          ? `${user.last_name} ${user.first_name} ${user.middle_name || ''}`.trim()
          : user.name || `Пользователь #${user.user_id}`
        }
        description="Детальная информация о пользователе"
      >
        <Link to="/couriers">
          <Button variant="secondary" size="sm">
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Назад к списку
          </Button>
        </Link>
      </PageHeader>

      
      <div className="flex-1 p-6 overflow-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Основная информация */}
          <div className="lg:col-span-2 space-y-6">
            {/* Общие данные */}
            <Card>
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Общая информация</h3>
              </div>
              <div className="px-6 py-4">
                <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">ID пользователя</dt>
                    <dd className="mt-1 text-sm text-gray-900">{user.user_id}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Логин</dt>
                    <dd className="mt-1 text-sm text-gray-900">{user.login || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Роль</dt>
                    <dd className="mt-1">
                      <StatusBadge status={user.role} type="role" />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Статус</dt>
                    <dd className="mt-1">
                      <StatusBadge status={user.status} type="status" />
                    </dd>
                  </div>
                </dl>
              </div>
            </Card>

            {/* Полные данные курьера (паритет с карточкой заявки) */}
            {(user.role === USER_ROLE.COURIER || user.role === USER_ROLE.EXTERNAL_COURIER) && (
              <CourierDetailContent
                courier={user}
                history={history?.items}
                historyLoading={isHistoryLoading}
              />
            )}

            {/* Тариф и начисления курьера */}
            {tariffSummary &&
              (user.role === USER_ROLE.COURIER || user.role === USER_ROLE.EXTERNAL_COURIER) && (
              <Card>
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900">Тариф и начисления</h3>
                </div>
                <div className="px-6 py-4">
                  <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Тариф</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {tariffSummary.tariff.mode === 'individual'
                          ? tariffSummary.tariff.name
                          : 'Авто — общий'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Операций</dt>
                      <dd className="mt-1 text-sm text-gray-900">{tariffSummary.operations_count}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Начислено сегодня</dt>
                      <dd className="mt-1 text-sm text-gray-900">{tariffSummary.accrued_today} ₽</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Начислено за период</dt>
                      <dd className="mt-1 text-sm text-gray-900">{tariffSummary.accrued_period} ₽</dd>
                    </div>
                  </dl>
                </div>
              </Card>
            )}
          </div>
          
          {/* Действия */}
          <div>
            <Card>
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Действия</h3>
              </div>
              <div className="px-6 py-4 space-y-3">
                {(user.role === USER_ROLE.COURIER || user.role === USER_ROLE.EXTERNAL_COURIER) &&
                  hasPermission('couriers', 'change_role') && (
                  <div className="pb-3 mb-1 border-b border-gray-100">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Тип курьера
                    </label>
                    <div className="flex items-center space-x-2">
                      <select
                        className="admin-input flex-1"
                        value={selectedRole}
                        onChange={(e) => setSelectedRole(Number(e.target.value))}
                      >
                        <option value={USER_ROLE.COURIER}>Штатный</option>
                        <option value={USER_ROLE.EXTERNAL_COURIER}>Сторонний</option>
                      </select>
                      <Button
                        variant="secondary"
                        onClick={handleRoleChange}
                        isLoading={updateUserRole.isPending}
                        disabled={updateUserRole.isPending || selectedRole === user.role}
                      >
                        Сменить
                      </Button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Недоступно при наличии активных заказов
                    </p>
                  </div>
                )}

                {user.status !== USER_STATUS.ACTIVE && canChangeStatus && (
                  <Button 
                    variant="primary" 
                    className="w-full"
                    onClick={() => handleStatusChange(
                      USER_STATUS.ACTIVE,
                      user.status === USER_STATUS.BLOCKED
                        ? 'Разблокирован администратором'
                        : 'Активирован администратором'
                    )}
                    isLoading={isUpdating}
                    disabled={isUpdating}
                  >
                    {user.status === USER_STATUS.BLOCKED ? 'Разблокировать' : 'Активировать'}
                  </Button>
                )}
                
                {user.status !== USER_STATUS.BLOCKED && canChangeStatus && (
                  <Button 
                    variant="secondary" 
                    className="w-full"
                    onClick={() => handleStatusChange(USER_STATUS.BLOCKED, 'Заблокирован администратором')}
                    isLoading={isUpdating}
                    disabled={isUpdating}
                  >
                    Заблокировать
                  </Button>
                )}
                
                {user.status !== USER_STATUS.DELETED && canChangeStatus && (
                  <Button 
                    variant="danger" 
                    className="w-full"
                    onClick={() => handleStatusChange(USER_STATUS.DELETED, 'Удален администратором')}
                    isLoading={isUpdating}
                    disabled={isUpdating}
                  >
                    Удалить
                  </Button>
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>
    </>
  )
}
